import {
  ADD_BOX,
  ADD_RICH_MARK,
  DELETE_BOX,
  DELETE_CONTAINED_VIEW,
  DELETE_NAV_ITEM,
  DELETE_SORTABLE_CONTAINER,
  RESIZE_BOX,
  ROTATE_BOX,
  RESIZE_SORTABLE_CONTAINER,
  TOGGLE_TEXT_EDITOR,
  UPDATE_BOX,
  UPDATE_PLUGIN_TOOLBAR,
  APPLY_IMAGE_URL_MAP,
  VERTICALLY_ALIGN_BOX,
  IMPORT_STATE,
  PASTE_BOX,
  ADD_NAV_ITEM,
  DUPLICATE_NAV_ITEM,
  DUPLICATE_NAV_ITEM_BY_PAYLOAD,
  IMPORT_EDI,
  SYNC_SCENE_SUCCESS,
  MIGRATE_LEGACY_GUIDES,
  MIGRATE_LEGACY_SOUND_LISTS,
  PASTE_SCENE,
  APPLY_AIDEA_SMART_ELEMENTS,
  // === Task 23 ===
  ADD_PLUGIN_TOOLBAR_ITEM,
  UPDATE_PLUGIN_TOOLBAR_ITEM,
  REMOVE_PLUGIN_TOOLBAR_ITEM,
  // === /Task 23 ===
  // === Task 5 — canvas-first interactive items ===
  ADD_INTERACTIVE_ITEM,
  UPDATE_ITEM_POSITION,
  UPDATE_ITEM_PARAM,
  REMOVE_INTERACTIVE_ITEM,
  // === /Task 5 ===
  // === Scenario-based Interactive System ===
  ADD_SCENE,
  REMOVE_SCENE,
  UPDATE_SCENE,
  ADD_SCENARIO_ELEMENT,
  REMOVE_SCENARIO_ELEMENT,
  UPDATE_SCENARIO_ELEMENT,
  UPDATE_SCENARIO_META,
  SET_SCENARIO_VARIABLES,
  // === /Scenario-based Interactive System ===
  BATCH_SET_SCENE_FONT,
} from "../common/actions";
import Utils, { changeProps, deleteProps, isSortableBox, isPage } from "../common/utils";
import { applyUrlMapToPluginToolbars } from "./applyImageUrlMap";
import { forceHtmlCssProperty } from "../common/common_tools";
import { migrateLegacyGuides } from "../common/migrate_legacy_guides.es6";
import { pinAudioCueStarts } from "../common/audio_cue_manual.es6";
import { migrateLegacySoundLists } from "../common/migrate_legacy_sound_lists.es6";
import { computeNonOverlappingPosition } from "../_editor/components/canvas_workflow/computeNonOverlappingPosition";
import i18n from "i18next";

// Canonicalise an InteractivePlugin sub-item shape:
//   1. If kind-specific fields live at the top level (legacy seed shape) and
//      there's no `params` object, wrap them into `params`.
//   2. If position lives only inside `params.position`, hoist it to the
//      top level — UPDATE_ITEM_POSITION and ItemFrame both treat top-level
//      `position` as canonical.
// Returns the original reference unchanged when no normalization applies, so
// reducers that call this in a .map() preserve referential equality where they
// can.
export function normalizeInteractiveItemShape(item) {
  if (!item || typeof item !== "object") return item;
  let next = item;
  if (next.id && next.kind && !next.params) {
    const { id, kind, position, ...rest } = next;
    if (Object.keys(rest).length > 0) {
      next = position ? { id, kind, position, params: rest } : { id, kind, params: rest };
    }
  }
  if (next && !next.position && next.params && next.params.position) {
    const { position: paramsPos, ...paramsRest } = next.params;
    next = { ...next, position: paramsPos, params: paramsRest };
  }
  return next;
}

// Walk a plugin-toolbars-by-id map and normalize each InteractivePlugin box's
// items. Used by IMPORT_STATE / IMPORT_EDI so saved content with the legacy
// params.position shape rehydrates into the canonical top-level-position
// shape that ItemFrame's drag math expects.
function normalizeInteractiveItemsInToolbars(toolbars) {
  if (!toolbars || typeof toolbars !== "object") return toolbars;
  let changed = false;
  const out = {};
  for (const id in toolbars) {
    const tb = toolbars[id];
    // Only InteractivePlugin uses the canonical `params`/`position` sub-item
    // shape this hoist targets. Other plugins that keep a FLAT `state.items`
    // list — notably GuideMultiInput, whose click/input/tag items store
    // x/y/width/height at the top level (see stepToBoxes.js) — must be left
    // untouched. Wrapping their fields into `params` makes item.x undefined,
    // so GuideMultiInputRenderer.itemStyle renders every item at
    // "undefinedpx" and the imported events vanish on reload (IMPORT_STATE).
    if (!tb || tb.pluginId !== "InteractivePlugin") {
      out[id] = tb;
      continue;
    }
    const items = tb.state && tb.state.items;
    if (!Array.isArray(items) || items.length === 0) {
      out[id] = tb;
      continue;
    }
    let itemsChanged = false;
    const nextItems = items.map((it) => {
      const normalized = normalizeInteractiveItemShape(it);
      if (normalized !== it) itemsChanged = true;
      return normalized;
    });
    if (itemsChanged) {
      changed = true;
      out[id] = { ...tb, state: { ...tb.state, items: nextItems } };
    } else {
      out[id] = tb;
    }
  }
  return changed ? out : toolbars;
}

function toolbarCreator(state, action) {
  let structure;
  let toolbar = {};
  if (isSortableBox(action.payload.ids.id)) {
    toolbar = {
      [action.payload.ids.id]: {
        id: action.payload.ids.id,
        pluginId: "sortable_container",
        state: {},
        structure: {},
        style: {},
      },
    };
  } else {
    let pluginId = action.payload.initialParams.name;
    if (!action.payload.ids.container !== 0) {
      if (state[action.payload.container]) {
        let toolbar_container = {
          id: action.payload.id.container,
          pluginId: "sortableBox",
          state: {},
          structure: {},
          style: {},
        };
        toolbar = { ...toolbar, toolbar_container };
      }
    }
    let initialWidth = 20;
    let widthUnit = "%";
    if (action.payload.initialParams.width) {
      let parsed = parseFloat(action.payload.initialParams.width);
      if (!isNaN(parsed)) {
        initialWidth = parsed;
        if (action.payload.initialParams.width.toString().indexOf("px") > -1) {
          widthUnit = "px";
        }
      } else if (action.payload.initialParams.width === "auto") {
        initialWidth = "auto";
      }
    }

    let initialHeight = "auto";
    let heightUnit = action.payload.resizable ? "%" : "px";

    if (action.payload.initialParams.height && action.payload.initialParams.height !== "auto") {
      let parsed = parseFloat(action.payload.initialParams.height);
      if (!isNaN(parsed)) {
        initialHeight = parsed;
        if (action.payload.initialParams.height.toString().indexOf("%") > -1) {
          heightUnit = "%";
        }
      } else if (action.payload.initialParams.height === "auto") {
        initialHeight = "auto";
      }
    }

    structure = {
      height: initialHeight,
      width: initialWidth,
      widthUnit: widthUnit,
      heightUnit: heightUnit,
      rotation: action.payload.initialParams.rotation || 0,
      horizontalFlip: action.payload.initialParams.horizontalFlip,
      verticalFlip: action.payload.initialParams.verticalFlip,
      aspectRatio:
        action.payload.initialParams.aspectRatio &&
        action.payload.initialParams.aspectRatio.defaultValue !== undefined
          ? action.payload.initialParams.aspectRatio.defaultValue
          : false,
      position: action.payload.id ? "relative" : "absolute",
    };

    toolbar = {
      ...toolbar,
      [action.payload.ids.id]: {
        id: action.payload.ids.id,
        pluginId: pluginId,
        state: action.payload.state || {},
        structure: structure || {},
        style: action.payload.style || {},
        showTextEditor: false,
      },
    };
  }
  return toolbar;
}

// Course-wide font: writes `textFontFamily` on every BasicText/RichText box
// AND forces `font-family` onto every element of its stored `__text`. The
// second half is what makes it actually visible — a per-span style CKEditor
// (or an earlier per-box format change) left behind out-ranks anything the
// box wrapper cascades down, so rewriting the content itself is the only
// reliable way to retypeset existing text. Same mechanism the single-box pill
// and the multi-select bar use (EditorShortcuts' applyTextFormatValue /
// EditorApp's applyMultiTextValue), just applied to the whole training at
// once. `boxIds` narrows it to one scene's boxes (the canvas font button's
// "this scene" scope); omitted means every text box in the training. Exported
// so the SaveAs path (EditorApp) can reuse it on a state tree it builds
// outside the reducer.
export function applyCourseFontToToolbars(state, fontFamily, boxIds) {
  if (!fontFamily || !state) return state;
  const next = { ...state };
  for (const id of boxIds || Object.keys(state)) {
    const toolbar = state[id];
    if (!toolbar) continue;
    // The free-response answer box stores its own font (default "Arial").
    if (toolbar.pluginId === "FreeResponse") {
      next[id] = { ...toolbar, state: { ...toolbar.state, inputFontFamily: fontFamily } };
      continue;
    }
    if (toolbar.pluginId !== "BasicText" && toolbar.pluginId !== "RichText") continue;
    const boxState = { ...toolbar.state, textFontFamily: fontFamily };
    if (boxState.__text) {
      boxState.__text = forceHtmlCssProperty(boxState.__text, "font-family", fontFamily);
    }
    next[id] = { ...toolbar, state: boxState };
  }
  return next;
}

export default function (state = {}, action = {}) {
  let newState;
  switch (action.type) {
    case ADD_BOX:
      let a = { ...state, ...toolbarCreator(state, action) };
      return a;
    case ADD_NAV_ITEM:
      if (action.payload.type === "document") {
        return {
          ...state,
          [action.payload.sortable_id]: {
            id: action.payload.sortable_id,
            pluginId: "sortable_container",
            showTextEditor: false,
            state: {},
            structure: {
              aspectRatio: false,
              height: "",
              position: "absolute",
              rotation: "",
              horizontalFlip: false,
              verticalFlip: false,
              width: "",
            },
            style: {
              backgroundColor: "#ffffff",
              boderWidth: 0,
              borderColor: "#000000",
              borderRadius: 0,
              borderStyle: "solid",
              opacity: 1,
              padding: 0,
            },
          },
        };
      }
      return state;
    case ADD_RICH_MARK:
      if (action.payload.mark.connectMode === "new" && action.payload.view.type === "document") {
        return {
          ...state,
          [action.payload.view.boxes[0]]: {
            id: action.payload.view.boxes[0],
            pluginId: "sortable_container",
            showTextEditor: false,
            state: {},
            structure: {
              aspectRatio: false,
              height: "",
              position: "absolute",
              rotation: "",
              horizontalFlip: false,
              verticalFlip: false,
              width: "",
            },
            style: {
              backgroundColor: "#ffffff",
              boderWidth: 0,
              borderColor: "#000000",
              borderRadius: 0,
              borderStyle: "solid",
              opacity: 1,
              padding: 0,
            },
          },
        };
      }
      return state;
    // return changeProp(state, action.payload.id, toolbarSortableContainer(state, action));
    case DELETE_BOX:
      let children = action.payload.children ? action.payload.children : [];
      return deleteProps(state, children.concat(action.payload.id));
    case DELETE_CONTAINED_VIEW:
      let boxesCV = action.payload.boxes ? action.payload.boxes : [];
      let newToolbarCV = JSON.parse(JSON.stringify(state));
      let parents = action.payload.parent ? action.payload.parent : [];
      // Delete all related marks

      return deleteProps(newToolbarCV, boxesCV.concat(action.payload.ids[0]));
    case DELETE_NAV_ITEM:
      let boxes = action.payload.boxes ? action.payload.boxes : [];
      let newToolbar = { ...state };

      return deleteProps(newToolbar, boxes.concat(action.payload.ids));
    case DELETE_SORTABLE_CONTAINER:
      return deleteProps(state, action.payload.children);
    case RESIZE_BOX:
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          structure: {
            ...state[action.payload.id].structure,
            ...action.payload.structure,
          },
        },
      };
    case ROTATE_BOX:
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          structure: {
            ...state[action.payload.id].structure,
            ...action.payload.structure,
          },
        },
      };
    case RESIZE_SORTABLE_CONTAINER:
      return {
        ...state,
        [action.payload.id]: {
          style: {
            height: action.payload.height,
          },
        },
      };
    case TOGGLE_TEXT_EDITOR:
      let idCaller = action.payload.id || action.payload.caller;
      return {
        ...state,
        [idCaller]: { ...state[idCaller], showTextEditor: action.payload.value },
      };
    case UPDATE_BOX:
      return {
        ...state,
        [action.payload.id]: { ...state[action.payload.id], state: action.payload.state },
      };
    //   return changeProp(state, action.payload.id, toolbarReducer(state[action.payload.id], action));
    case UPDATE_PLUGIN_TOOLBAR:
      let newValues = {};
      if (action.payload.name instanceof Array) {
        action.payload.name.map((name, ind) => {
          newValues[name] = action.payload.value[ind];
        });
      } else {
        newValues[action.payload.name] = action.payload.value;
      }
      let updatedState = {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          [action.payload.accordion]: {
            ...state[action.payload.id][action.payload.accordion],
            ...newValues,
          },
        },
      };
      for (let b in action.payload.deletedBoxes) {
        delete updatedState[action.payload.deletedBoxes[b]];
      }
      return updatedState;
    case APPLY_IMAGE_URL_MAP:
      // Bulk logo: swap every scene image plugin's url/src from the job urlMap.
      return applyUrlMapToPluginToolbars(state, action.payload.urlMap);
    case VERTICALLY_ALIGN_BOX:
      return {
        ...state,
        [action.payload.id]: {
          style: {
            verticalAlign: action.payload.value,
          },
        },
      };
    case IMPORT_STATE:
      // Run the same item-shape hoist used at ADD_INTERACTIVE_ITEM time over
      // any rehydrated interactive items, so legacy `params.position`-only
      // content (or items that have kind-specific fields at the top level)
      // loads with the canonical top-level `position` ItemFrame's drag math
      // depends on. Without this, dragging a pre-existing item on a loaded
      // training silently no-ops (NaN style values, no commit).
      return normalizeInteractiveItemsInToolbars(
        pinAudioCueStarts(
          action.payload.present.pluginToolbarsById || state,
          action.payload.present.boxesById
        )
      );
    case PASTE_BOX:
      return changeProps(
        state,
        [action.payload.ids.id, ...Object.keys(action.payload.children)],
        [
          action.payload.toolbar,
          ...Object.keys(action.payload.children).map((k) => {
            return action.payload.children[k].toolbar;
          }),
        ]
      );
    case DUPLICATE_NAV_ITEM:
      let newToolbars = {};
      for (let t in action.payload.boxes) {
        let nt = action.payload.boxes[t];
        newToolbars[nt] = { ...state[t], id: nt };
      }
      return { ...state, ...newToolbars };
    case DUPLICATE_NAV_ITEM_BY_PAYLOAD:
      let newToolbars2 = {};
      action.payload.boxes.forEach((box) => {
        newToolbars2[box.id] = { ...box.pluginToolbar, id: box.id };
      });

      return { ...state, ...newToolbars2 };
    case PASTE_SCENE:
      return { ...state, ...action.payload.toolbars };
    case IMPORT_EDI:
      // Same hoist as IMPORT_STATE — .edi imports are another rehydration
      // path for previously-saved interactive items.
      return {
        ...state,
        ...normalizeInteractiveItemsInToolbars(
          pinAudioCueStarts(action.payload.state.pluginToolbarsById, action.payload.state.boxesById)
        ),
      };
    case SYNC_SCENE_SUCCESS: {
      if (!action.payload.pluginToolbarsById) return state;
      let merged = { ...state };
      Object.keys(action.payload.pluginToolbarsById).forEach((id) => {
        if (merged[id]) {
          merged[id] = { ...merged[id], ...action.payload.pluginToolbarsById[id] };
        }
      });
      return merged;
    }
    case BATCH_SET_SCENE_FONT:
      return applyCourseFontToToolbars(state, action.payload.fontFamily, action.payload.boxIds);
    case MIGRATE_LEGACY_GUIDES: {
      const out = migrateLegacyGuides({
        boxesById: action.payload.boxesById || {},
        pluginToolbarsById: state,
        navItemsById: action.payload.navItemsById || {},
        containedViewsById: action.payload.containedViewsById || {},
      });
      return out.pluginToolbarsById;
    }
    case MIGRATE_LEGACY_SOUND_LISTS:
      return migrateLegacySoundLists({ ...action.payload, pluginToolbarsById: state })
        .pluginToolbarsById;
    case APPLY_AIDEA_SMART_ELEMENTS: {
      const ls = action.payload;
      if (!ls || !Array.isArray(ls.layers)) return state;
      const next = { ...state };
      for (const layer of ls.layers) {
        // Every element (background / subject / text) is an IMAGE (HotspotImages).
        // A text element is a faithful image CROP of the label and additionally
        // carries `recognizedText` (the OCR'd words) so the editor can show the
        // text on click / hand it to AI for editing — no font distortion.
        next[layer.id] = {
          id: layer.id,
          pluginId: "HotspotImages",
          state: {
            url: layer.url,
            scale: 1,
            // allowDeformed=false → PNG keeps its natural aspect inside the box,
            // letterboxed if needed. Without this, qwen's 1:1 output gets stretched
            // to the slide-box's 16:9 footprint and subjects look squashed.
            allowDeformed: false,
            ...(layer.kind === "text" && layer.recognizedText
              ? { isText: true, recognizedText: layer.recognizedText }
              : {}),
          },
          structure: layer.structure,
          style: {
            backgroundColor: "rgba(255,255,255,0)",
            borderWidth: 0,
            borderColor: "#000000",
            borderStyle: "solid",
            borderRadius: 0,
            opacity: 1,
            padding: 0,
          },
          showTextEditor: false,
        };
      }
      return next;
    }
    // === Task 23 === AKIŞ ÖĞELERİ per-item editing (InteractivePlugin)
    case ADD_PLUGIN_TOOLBAR_ITEM: {
      const { boxId, item } = action.payload;
      const tb = state[boxId];
      if (!tb) return state;
      const items = (tb.state && tb.state.items) || [];
      return {
        ...state,
        [boxId]: { ...tb, state: { ...tb.state, items: [...items, item] } },
      };
    }
    case UPDATE_PLUGIN_TOOLBAR_ITEM: {
      const { boxId, itemId, patch } = action.payload;
      const tb = state[boxId];
      if (!tb) return state;
      const items = (tb.state && tb.state.items) || [];
      const updated = items.map((it) =>
        it && it.id === itemId
          ? {
              ...it,
              ...patch,
              params: { ...(it.params || {}), ...((patch && patch.params) || {}) },
            }
          : it
      );
      return {
        ...state,
        [boxId]: { ...tb, state: { ...tb.state, items: updated } },
      };
    }
    case REMOVE_PLUGIN_TOOLBAR_ITEM: {
      const { boxId, itemId } = action.payload;
      const tb = state[boxId];
      if (!tb) return state;
      const items = (tb.state && tb.state.items) || [];
      return {
        ...state,
        [boxId]: {
          ...tb,
          state: { ...tb.state, items: items.filter((it) => it && it.id !== itemId) },
        },
      };
    }
    // === /Task 23 ===
    // === Task 5 — canvas-first interactive items ===
    case ADD_INTERACTIVE_ITEM: {
      const { boxId, item } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      // Canonicalize the item shape on ingest so every downstream reader
      // (ItemFrame, buildElementGraph, ElementNode preview, persistence layer)
      // sees a single source of truth. See normalizeInteractiveItemShape:
      //   1. wrap top-level kind-specific fields into `params`
      //   2. hoist legacy `params.position` to top-level `position`
      // (Mirrors commit 7a29a531 — the position hoist — and extends it to
      // the rest of the param surface.)
      const normalizedItem = normalizeInteractiveItemShape(item);
      // Round 36 — smart auto-position. Without this, sequentially adding
      // multiple items lands them all at defaultParamsFor(kind).position
      // and authors have to drag each one off the stack. The helper finds
      // the nearest non-overlapping slot by stepping down/right.
      const existingItems = (box.state && box.state.items) || [];
      const placedItem = normalizedItem.position
        ? {
            ...normalizedItem,
            position: computeNonOverlappingPosition(existingItems, normalizedItem.position),
          }
        : normalizedItem;
      const items = [...existingItems, placedItem];
      return { ...state, [boxId]: { ...box, state: { ...box.state, items } } };
    }
    case UPDATE_ITEM_POSITION: {
      const { boxId, itemId, position } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      // Write the canonical top-level path AND clear any stale
      // params.position so later reads can't pick a divergent legacy value.
      const items = ((box.state && box.state.items) || []).map((it) => {
        if (!it || it.id !== itemId) return it;
        if (it.params && it.params.position) {
          const { position: _stale, ...paramsRest } = it.params;
          return { ...it, position, params: paramsRest };
        }
        return { ...it, position };
      });
      return { ...state, [boxId]: { ...box, state: { ...box.state, items } } };
    }
    case UPDATE_ITEM_PARAM: {
      const { boxId, itemId, path, value } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      if (!Array.isArray(path) || path.length === 0) return state;
      const items = ((box.state && box.state.items) || []).map((it) => {
        if (!it || it.id !== itemId) return it;
        // Walk the path, cloning each segment along the way.
        const next = Array.isArray(it) ? [...it] : { ...it };
        let cursor = next;
        for (let i = 0; i < path.length - 1; i++) {
          const key = path[i];
          const child = cursor[key];
          cursor[key] = Array.isArray(child)
            ? [...child]
            : child && typeof child === "object"
              ? { ...child }
              : {};
          cursor = cursor[key];
        }
        cursor[path[path.length - 1]] = value;
        return next;
      });
      return { ...state, [boxId]: { ...box, state: { ...box.state, items } } };
    }
    case REMOVE_INTERACTIVE_ITEM: {
      const { boxId, itemId } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      return {
        ...state,
        [boxId]: {
          ...box,
          state: {
            ...box.state,
            items: ((box.state && box.state.items) || []).filter((it) => !it || it.id !== itemId),
          },
        },
      };
    }
    // === /Task 5 ===

    // === Scenario-based Interactive System ===
    case UPDATE_SCENARIO_META: {
      const { boxId, patch } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      const scenario = (box.state && box.state.scenario) || {};
      return {
        ...state,
        [boxId]: { ...box, state: { ...box.state, scenario: { ...scenario, ...patch } } },
      };
    }
    case SET_SCENARIO_VARIABLES: {
      const { boxId, variables } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      const scenario = (box.state && box.state.scenario) || {};
      return {
        ...state,
        [boxId]: { ...box, state: { ...box.state, scenario: { ...scenario, variables } } },
      };
    }
    case ADD_SCENE: {
      const { boxId, scene } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      const scenario = (box.state && box.state.scenario) || {};
      const scenes = [...(scenario.scenes || []), scene];
      // If startSceneId is missing (e.g. stale data), anchor to the first scene
      const startSceneId = scenario.startSceneId || (scenes[0] && scenes[0].id) || null;
      return {
        ...state,
        [boxId]: {
          ...box,
          state: { ...box.state, scenario: { ...scenario, scenes, startSceneId } },
        },
      };
    }
    case REMOVE_SCENE: {
      const { boxId, sceneId } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      const scenario = (box.state && box.state.scenario) || {};
      const scenes = (scenario.scenes || []).filter((s) => s.id !== sceneId);
      // If the removed scene was startSceneId, reset to first remaining scene
      const startSceneId =
        scenario.startSceneId === sceneId
          ? (scenes[0] && scenes[0].id) || null
          : scenario.startSceneId;
      return {
        ...state,
        [boxId]: {
          ...box,
          state: { ...box.state, scenario: { ...scenario, scenes, startSceneId } },
        },
      };
    }
    case UPDATE_SCENE: {
      const { boxId, sceneId, patch } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      const scenario = (box.state && box.state.scenario) || {};
      const scenes = (scenario.scenes || []).map((s) =>
        s.id === sceneId ? { ...s, ...patch } : s
      );
      return {
        ...state,
        [boxId]: { ...box, state: { ...box.state, scenario: { ...scenario, scenes } } },
      };
    }
    case ADD_SCENARIO_ELEMENT: {
      const { boxId, sceneId, element } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      const scenario = (box.state && box.state.scenario) || {};
      const scenes = (scenario.scenes || []).map((s) => {
        if (s.id !== sceneId) return s;
        return { ...s, elements: [...(s.elements || []), element] };
      });
      return {
        ...state,
        [boxId]: { ...box, state: { ...box.state, scenario: { ...scenario, scenes } } },
      };
    }
    case REMOVE_SCENARIO_ELEMENT: {
      const { boxId, sceneId, elementId } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      const scenario = (box.state && box.state.scenario) || {};
      const scenes = (scenario.scenes || []).map((s) => {
        if (s.id !== sceneId) return s;
        return { ...s, elements: (s.elements || []).filter((e) => e.id !== elementId) };
      });
      return {
        ...state,
        [boxId]: { ...box, state: { ...box.state, scenario: { ...scenario, scenes } } },
      };
    }
    case UPDATE_SCENARIO_ELEMENT: {
      const { boxId, sceneId, elementId, path, value } = action.payload;
      const box = state[boxId];
      if (!box) return state;
      if (!Array.isArray(path) || path.length === 0) return state;
      const scenario = (box.state && box.state.scenario) || {};
      const scenes = (scenario.scenes || []).map((s) => {
        if (s.id !== sceneId) return s;
        const elements = (s.elements || []).map((el) => {
          if (el.id !== elementId) return el;
          // Deep-clone the element and walk the path
          const next = { ...el };
          let cursor = next;
          for (let i = 0; i < path.length - 1; i++) {
            const key = path[i];
            const child = cursor[key];
            cursor[key] = Array.isArray(child) ? [...child] : { ...(child || {}) };
            cursor = cursor[key];
          }
          cursor[path[path.length - 1]] = value;
          return next;
        });
        return { ...s, elements };
      });
      return {
        ...state,
        [boxId]: { ...box, state: { ...box.state, scenario: { ...scenario, scenes } } },
      };
    }
    // === /Scenario-based Interactive System ===

    default:
      return state;
  }
}
